import { useId } from 'react';
import { Checkbox } from '@/components/ui';
import { SMS_CONSENT_TEXT } from '@/config/smsConsent';

export interface SmsConsentDisclosureProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

/**
 * The SMS/RCS opt-in block, shared by the onboarding digest step and
 * Settings › Notifications (YAR-459 / YAR-460).
 *
 * Twilio's RCS sender review reads this block as the evidence of consent, so
 * it is one component rather than two flows keeping the same parts in sync
 * by hand: the exact ledger sentence (`SMS_CONSENT_TEXT`, the string that
 * `notification_consent_events` stores under `SMS_CONSENT_VERSION`) as the
 * checkbox label, the box unchecked until the parent ticks it, and the
 * Terms of Service / Privacy Policy links right under the disclosure. The
 * frequency, rates, STOP and HELP disclosures are part of the sentence
 * itself. Settings used to show the sentence in an info box and a checkbox
 * with a different, shorter label — a second version of the consent
 * language, which the review disallows.
 *
 * Layout is the onboarding step's (checkbox + label row, then the caption
 * line): the links open in a new tab because a mid-flow route swap lost the
 * step's state (YAR-278).
 */
export function SmsConsentDisclosure({
  checked,
  onChange,
}: SmsConsentDisclosureProps) {
  const id = useId();
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <Checkbox
          id={id}
          checked={checked}
          onChange={onChange}
        />
        <label
          htmlFor={id}
          className="cursor-pointer text-sm leading-5 text-foreground"
        >
          {SMS_CONSENT_TEXT}
        </label>
      </div>
      <p className="text-sm leading-5 text-text-caption">
        By continuing, you agree to our{' '}
        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-text-neutral-hover underline"
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-text-neutral-hover underline"
        >
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
}
